<template>
    <div>
        <app-layout>
            <!-- Header -->
            <template v-slot:header>
                <Header title="Co-op Sorumlusu/Etkinlik"
                        :isFilter="true"
                        @filter-div-status="datatable.filterStatus = $event"
                        :actionButtons="headerActionButtons" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile title="Co-op Sorumlusu/Etkinlik"
                              :isFilter="true"
                              @filter-div-status="datatable.filterStatus = $event"
                              :actionButtons="headerActionButtons" />
            </template>

            <!-- Filters -->
            <datatable-filter v-show="datatable.filterStatus"
                              :exportExcel="true"
                              :jsonExcelButton="true"
                              :jsonExcelData="datatable.rows"
                              :jsonExcelFields="excelFields"
                              @filter="filter"
                              @filterClear="filterClear">
                <b-row>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('company_name')">
                            <b-form-input v-model="datatable.queryParams.filter.company_name" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('coop_start_date')">
                            <b-form-input type="date" v-model="datatable.queryParams.filter.start_date" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('status')">
                            <b-form-select v-model="datatable.queryParams.filter.status" :options="statusOptions">
                                <template #first>
                                    <b-form-select-option :value="null">-- {{ $t('status') }} --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('academic_year')">
                            <b-form-input v-model="datatable.queryParams.filter.academic_year" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="3" sm="6" class="mb-2">
                        <b-form-group :label="$t('semester') || 'Dönem'">
                            <b-form-input v-model="datatable.queryParams.filter.semester" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
                       title="COOP Business Point Katılımcılar"
            />
        </app-layout>

        <!-- Columns Toggle Modal -->
        <CommonModal ref="columnsModal" size="md" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_columns') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2">
                    <div v-for="col in togglableColumns" :key="col.field" class="mb-3">
                        <b-form-checkbox :checked="!col.hidden" @change="toggleColumnVisibility(col)">
                            {{ col.label }}
                        </b-form-checkbox>
                    </div>
                </div>
            </template>
        </CommonModal>

        <!-- New Session Modal -->
        <CommonModal ref="newSessionModal" size="md" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                Yeni Ekle
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-3">
                    <b-form @submit.prevent="saveNewSession">
                        <b-form-group label="Firma" class="mb-3">
                            <b-form-input v-model="newSessionForm.company_name" required />
                        </b-form-group>
                        <b-form-group label="Konuşmacı" class="mb-3">
                            <b-form-input v-model="newSessionForm.speaker" required />
                        </b-form-group>
                        <b-form-group label="Tarih" class="mb-3">
                            <b-form-input type="date" v-model="newSessionForm.date" required />
                        </b-form-group>
                        <b-form-group label="Fakülte" class="mb-3">
                            <b-form-input v-model="newSessionForm.faculty" />
                        </b-form-group>
                        <b-form-group label="Bölüm" class="mb-3">
                            <b-form-input v-model="newSessionForm.department" />
                        </b-form-group>
                        <div class="text-right">
                            <b-button type="submit" variant="primary" class="px-4">
                                Kaydet
                            </b-button>
                            <b-button variant="secondary" class="ml-2" @click="$refs.newSessionModal.$refs.commonModal.hide()">
                                İptal
                            </b-button>
                        </div>
                    </b-form>
                </div>
            </template>
        </CommonModal>

        <!-- Status Update Modal -->
        <CommonModal ref="statusUpdateModal" size="md" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_update_status') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2" v-if="selectedRow">
                    <b-form-group :label="$t('status')" class="font-weight-bold">
                        <b-form-select v-model="selectedRowStatus" :options="statusOptions" />
                    </b-form-group>
                    <b-button variant="primary" class="px-4 mt-2" @click="saveStatusUpdate">
                        {{ $t('save') }}
                    </b-button>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    import AppLayout from "@/layouts/AppLayout";
    import Header from "@/layouts/AppLayout/Header";
    import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";
    import Datatable from "@/components/datatable/Datatable";
    import DatatableFilter from "@/components/datatable/DatatableFilter";
    import CommonModal from "@/components/elements/CommonModal";
    import CoopBusinessPointService from "@/services/CoopBusinessPointService";
    import qs from "qs";

    export default {
        name: "CoopBusinessPointAdminIndex",
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            Datatable,
            DatatableFilter,
            CommonModal
        },
        data() {
            return {
                selectedRow: null,
                selectedRowStatus: "",
                newSessionForm: {
                    company_name: "",
                    speaker: "",
                    date: "",
                    faculty: "",
                    department: ""
                },
                excelFields: {
                    "Firma": "company_name",
                    "Konuşmacı": "speaker",
                    "Tarih": "date",
                    "Kontenjan": "quota",
                    "Durum": "status"
                },
                statusOptions: [
                    { value: "Aktif", text: "Aktif" },
                    { value: "Pasif", text: "Pasif" }
                ],
                datatable: {
                    isLoading: false,
                    filterStatus: true,
                    columns: [
                        {
                            label: "",
                            field: "buttons",
                            html: true,
                            hidden: false,
                            tdClass: "p-0 text-center w-40 align-middle",
                            buttons: [
                                {
                                    text: "Durum Güncelle",
                                    class: "ri-edit-line text-primary",
                                    callback: (row) => {
                                        this.openStatusUpdateModal(row);
                                    }
                                },
                                {
                                    text: "Katılımcılar",
                                    class: "ri-group-line text-info",
                                    callback: (row) => {
                                        this.$router.push(`/coop/business-point/${row.id}/participants`);
                                    }
                                },
                                {
                                    text: "Business Point Sil",
                                    class: "ri-delete-bin-line text-danger",
                                    callback: (row) => {
                                        this.deleteSession(row.id);
                                    }
                                }
                            ]
                        },
                        {
                            label: "Firma",
                            field: "company_name",
                            hidden: false
                        },
                        {
                            label: "Konuşmacı",
                            field: "speaker",
                            hidden: false
                        },
                        {
                            label: "Tarih",
                            field: "date",
                            hidden: false
                        },
                        {
                            label: "Kontenjan",
                            field: "quota",
                            hidden: false
                        },
                        {
                            label: "Durum",
                            field: "status",
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {
                            company_name: "",
                            start_date: "",
                            status: null,
                            academic_year: "",
                            semester: ""
                        },
                        sort: "id",
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        computed: {
            headerActionButtons() {
                return [
                    {
                        text: this.$t("coop_new_event"),
                        callback: this.openNewSessionModal
                    },
                    {
                        text: this.$t("coop_columns"),
                        callback: this.openColumnsModal
                    }
                ];
            },
            togglableColumns() {
                return this.datatable.columns.filter(col => col.field !== "buttons");
            }
        },
        methods: {
            toUpperCase(val) {
                return val ? val.toUpperCase() : "";
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == "desc" ? "-" : "";
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {
                    company_name: "",
                    start_date: "",
                    status: null,
                    academic_year: "",
                    semester: ""
                };
                this.getRows();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopBusinessPointService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination.total;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            openColumnsModal() {
                this.$refs.columnsModal.$refs.commonModal.show();
            },
            toggleColumnVisibility(col) {
                col.hidden = !col.hidden;
            },
            openNewSessionModal() {
                this.newSessionForm = {
                    company_name: "",
                    speaker: "",
                    date: "",
                    faculty: "",
                    department: ""
                };
                this.$refs.newSessionModal.$refs.commonModal.show();
            },
            saveNewSession() {
                CoopBusinessPointService.create(this.newSessionForm).then(() => {
                    this.$toast.success(this.$t("coop_event_updated_success"));
                    this.$refs.newSessionModal.$refs.commonModal.hide();
                    this.getRows();
                });
            },
            openStatusUpdateModal(row) {
                this.selectedRow = row;
                this.selectedRowStatus = row.status;
                this.$refs.statusUpdateModal.$refs.commonModal.show();
            },
            saveStatusUpdate() {
                CoopBusinessPointService.updateStatus(this.selectedRow.id, this.selectedRowStatus).then(() => {
                    this.$toast.success(this.$t("coop_status_updated_success") || "Durum güncellendi.");
                    this.$refs.statusUpdateModal.$refs.commonModal.hide();
                    this.getRows();
                });
            },
            deleteSession(id) {
                CoopBusinessPointService.remove(id).then(() => {
                    this.$toast.success("Business Point silindi.");
                    this.getRows();
                });
            }
        },
        created() {
            this.getRows();
        }
    };
</script>
